import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

// Types
import { GoalItem, GoalIcons } from 'Shared/constants/types';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useToggle } from 'Shared/utils/hooks';

// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpTextField } from 'Wpds/components/text-field';
import WpSelectV2, { WidthType, StrictOption } from 'Wpds/components/select/WpSelectV2';
import GoalIcon from 'Shared/components/Goals/GoalIcon';
import GoalStatus from '../GoalStatus/GoalStatus';

// Styles
import './GoalName.scss';

type Props = {
  goal: GoalItem;
  showIcon?: boolean;
  showLinkedIndicator?: boolean;
  showStatusIndicator?: boolean;
  linkToGoal?: boolean;
  size?: 'sm' | 'lg';
  truncateName?: boolean;
  editable?: boolean;
};

const GoalName = ({
  goal,
  showIcon = true,
  showLinkedIndicator = true,
  showStatusIndicator = false,
  linkToGoal = false,
  size = 'sm',
  truncateName = true,
  editable = false,
}: Props) => {
  const { canChangeStatus, isSaving, handleUpdateGoal } = useGoal({ slug: goal.slug });

  const [icon, setIcon] = useState<string>(goal.icon);
  const [value, setValue] = useState<string>(goal.name);
  const [isEditing, toggleIsEditing] = useToggle(false);

  const hasChildren = goal.childrensCount > 0;
  const iconOptions = Object.values(GoalIcons);
  const canUpdate = canChangeStatus && editable;

  const handleSaveClick = () => {
    if (value !== '') {
      handleUpdateGoal({ name: value, icon }).then(() => {
        toggleIsEditing();
      });
    }
  };

  const handleCancelClick = () => {
    toggleIsEditing();
    setIcon(goal.icon);
    setValue(goal.name);
  };

  const baseClassName = 'GoalName';
  const classes = classnames(
    baseClassName,
    truncateName ? `${baseClassName}--truncate` : '',
    canUpdate ? `${baseClassName}--editable` : '',
    `${baseClassName}--${size}`
  );

  const content = (
    <>
      {showIcon && (
        <span className={`${baseClassName}__icon`}>
          <GoalIcon icon={goal.icon} status={goal.status} size={size} />
        </span>
      )}
      <div className={`${baseClassName}__name`}>{goal.name}</div>
    </>
  );

  if (editable && isEditing) {
    return (
      <WpContainer>
        <WpContainerBody padding="05">
          <div className={`${baseClassName}__editable`}>
            <WpSelectV2
              id="icon-select"
              label="select icon"
              labelHidden
              options={iconOptions}
              onChange={(option: StrictOption) => setIcon(option.value)}
              optionsWidth={WidthType.target}
              disabled={isSaving}
              components={{
                innerDropdown: () => (
                  <WpButton square slimPadding color="gray" disabled={isSaving}>
                    <WpIcon name={icon} />
                    <WpIcon name="angle-down" />
                  </WpButton>
                ),
                option: ({ option }) => {
                  return (
                    <div className={`${baseClassName}__editable__icon-option`}>
                      <WpIcon name={option.value} size="md" />
                    </div>
                  );
                },
              }}
            />
            <div className={`${baseClassName}__editable__name`}>
              <WpTextField
                id="goal-name"
                label="goal name"
                labelHidden
                value={value}
                onChange={setValue}
                hideBorder
                max={100}
                autoFocus
                readOnly={isSaving}
              />
            </div>
          </div>
          <WpButtonGroup spacing="md">
            <WpButton size="xs" square slimPadding onClick={handleSaveClick} loading={isSaving}>
              Save
            </WpButton>
            <WpButton variant="tertiary" size="xs" square slimPadding onClick={handleCancelClick} disabled={isSaving}>
              Cancel
            </WpButton>
          </WpButtonGroup>
        </WpContainerBody>
      </WpContainer>
    );
  }

  return (
    <div className={classes} onClick={canUpdate ? toggleIsEditing : undefined}>
      {showStatusIndicator && <GoalStatus goal={goal} showCompleteIndicator={false} showLabel={false} />}
      {linkToGoal ? (
        <Link
          className={`${baseClassName}__link`}
          to={PageUrlsUtils.makeGoalDetailPageUrl(goal.slug)}
          onClick={e => e.stopPropagation()}
        >
          {content}
        </Link>
      ) : (
        content
      )}
      {showLinkedIndicator && hasChildren && (
        <div className={`${baseClassName}__nested-indicator`}>
          <WpIcon prefix="fac" name="nested-link" size="sm" />
          <span className={`${baseClassName}__nested-indicator__count`}>{goal.childrensCount}</span>
        </div>
      )}
    </div>
  );
};

export default GoalName;
